'use client';
import { useCallback, useEffect, useState } from 'react';
import useHuddle01 from './useHuddle01';
import type {
  CustomMediaKind,
  SupportedCodecs,
} from '@huddle01/web-core/types';
import type { Producer } from '@huddle01/web-core/types';

export const useLocalAudio = (props?: {
  // Callback to be called when the producer starts producing - i.e When inside the room and the producer is started
  onProduceStart?: (producer: Producer) => void;

  // Callback to be called when the producer stops producing - i.e When inside the room and the producer is stopped
  onProduceClose?: (label: string) => void;

  // Callback to be called when the producer stops producing - i.e When inside the room and the producer is stopped
  onProducePaused?: () => void;
}) => {
  const { huddleClient } = useHuddle01();

  const localPeer = huddleClient.localPeer;

  const [stream, setStream] = useState<MediaStream | null>(
    localPeer.getStream({ label: 'audio' }),
  );

  const [isAudioOn, setIsAudioOn] = useState<boolean>(
    stream?.getAudioTracks()?.[0]?.enabled ?? false,
  );

  const [isProducing, setIdProducing] = useState<boolean>(false);

  const handleStreamFetched = useCallback(
    (data: {
      mediaKind: CustomMediaKind;
      stream: MediaStream;
    }) => {
      if (data.mediaKind === 'mic') {
        setStream(data.stream);
        const isEnabled = data.stream.getAudioTracks()?.[0]?.enabled ?? false;
        setIsAudioOn(isEnabled);
      }
    },
    [],
  );

  const handleStreamPlayable = useCallback(
    (data: {
      label: string;
      mediaKind?: CustomMediaKind;
      producer: Producer;
    }) => {
      if (data.label === 'audio') {
        setStream(localPeer.getStream({ label: 'audio' }));
        setIsAudioOn(true);
        setIdProducing(true);

        if (props?.onProduceStart) props?.onProduceStart(data.producer);
      }
    },
    [props?.onProduceStart],
  );

  const handleStreamClosed = useCallback(
    (data: {
      label: string;
      mediaKind?: CustomMediaKind;
      reason?: { code: number; tag: string; message?: string };
    }) => {
      if (data.label === 'audio') {
        setStream(null);
        setIsAudioOn(false);
        setIdProducing(false);

        if (props?.onProduceClose) props?.onProduceClose(data.label);
      }
    },
    [props?.onProduceClose],
  );

  const handleStreamPaused = useCallback(
    (data: {
      label: string;
      mediaKind?: CustomMediaKind;
    }) => {
      if (data.label === 'audio') {
        setIsAudioOn(false);

        if (props?.onProducePaused) props?.onProducePaused();
      }
    },
    [],
  );

  useEffect(() => {
    localPeer.on('stream-fetched', handleStreamFetched);
    localPeer.on('stream-playable', handleStreamPlayable);
    localPeer.on('stream-closed', handleStreamClosed);
    localPeer.on('stream-paused', handleStreamPaused);

    return () => {
      localPeer.off('stream-fetched', handleStreamFetched);
      localPeer.off('stream-playable', handleStreamPlayable);
      localPeer.off('stream-closed', handleStreamClosed);
      localPeer.off('stream-paused', handleStreamPaused);
    };
  }, []);

  const track = stream?.getAudioTracks()[0] || null;

  /**
   * Enables the local mic and starts producing the stream with the label `audio`
   * @param customVideoStream (Optional) Custom Video Stream to produce, if not provided it will fetch the stream from the device
   * @param prefferedCodec (Optional) Preferred Codec to be used for the stream ( SupportedCodecs ), make sure the codec is supported by the device ( 'Browser' or 'ReactNative' )
   *
   * @summary This functions handle the producing of media streams to all the remote peers in the room.
   * it enables the local mic fetches the stream opens you mic active indicator light on the device
   * upon successfull fetching of the stream it produces the stream with the label `audio` and `stopTrackOnClose: true` to all the remote peers in the room.
   * when closing using `disableAudio` it will stop the local audio track and close the producer which will notify all the RemotePeers that this producer has stopped producing
   * and they should stop consuming it.
   *
   * `NOTE: You can only produce to a room when you have joined the room, if you try to produce before joining the room it will fetch the stream and start producing when you join the room`
   *
   * @throws { Error } If the stream is not found
   * @throws { Error } If Preffered Codec is not supported by the Device ( 'Browser' or 'ReactNative' )
   * @throws { Error } If Failed to produce, Reason will be in the error message
   */
  const enableAudio = useCallback(
    (data?: {
      customAudioStream?: MediaStream;
      prefferedCodec?: SupportedCodecs;
    }) => {
      return localPeer.enableAudio(data);
    },
    [localPeer],
  );

  /**
   * Stops the underlying producing of a microphone stream, stops the local track and closes the producer
   * NOTE: This will notify all the RemotePeers that this producer has stopped producing and they should stop consuming it.
   */
  const disableAudio = useCallback(() => {
    return localPeer.disableAudio();
  }, [localPeer]);

  /**
   * Pauses the audio stream, pauses the underlying producer but does not stop the local track
   * useful when user has joined the room and wants to pause the audio stream which is faster than disabling the audio stream
   * NOTE: Only use this function when peer has joined the room else use disableAudio,
   * The LocalPeer will not be producing any audio stream but the local track will still be active,
   * you can resume the audio stream using resumeAudio or stop the audio stream using disableAudio
   */
  const pauseAudio = useCallback(() => {
    return localPeer.pauseAudio();
  }, [localPeer]);

  /**
   * Resumes the audio stream, resumes the underlying producer and starts producing the audio stream
   * useful when user has joined the room and wants to resume the audio stream
   * Note: Works only if the audio stream is paused using pauseAudio
   */
  const resumeAudio = useCallback(() => {
    return localPeer.resumeAudio();
  }, [localPeer]);

  /**
   * Replaces the current audio stream with the new stream if you have produced a stream with label audio or
   * used the default function enableAudio and you want to replace it with a new stream
   * @param stream — New Audio Stream
   */
  const replaceAudioStream = useCallback(
    (stream: MediaStream) => {
      return localPeer.replaceAudioStream(stream);
    },
    [localPeer],
  );

  /**
   * Changes the Audio source to the given deviceId, sets the preferred mic device as the given deviceId
   */
  const changeAudioSource = useCallback(
    (deviceId: string) => {
      return localPeer.changeAudioSource(deviceId);
    },
    [localPeer],
  );

  return {
    stream,
    track,
    isAudioOn,
    isProducing,
    enableAudio,
    disableAudio,
    pauseAudio,
    resumeAudio,
    replaceAudioStream,
    changeAudioSource,
  };
};
